import {
  ArchiveBoxIcon,
  ArrowUpCircleIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  NoSymbolIcon,
  PencilIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { RiAdminLine, RiUserLine, RiUserSettingsLine } from 'react-icons/ri';
import { FaQuestion } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import { BiQuestionMark } from 'react-icons/bi';

interface IStatus {
  status?: string | null;
  icon?: any;
  small?: boolean;
  useTooltip?: boolean;
}

function Status({ status, icon, small = false, useTooltip = false }: IStatus) {
  const { t } = useTranslation();
  if (!status) {
    return null;
  }

  let style = 'bg-slate-500';
  let logoStyle = 'w-5 h-5';
  let text = t(`status.${status}`);

  let Icon = icon;
  let noText = false;
  let baseStyle = small ? 'py-1 px-2 h-6 text-[0.8rem]' : 'py-1.5 px-4 text-[1rem]';

  // Status for contents

  if (status === 'published') {
    style = 'bg-green-500';
    Icon = CheckIcon;
  }

  if (status === 'draft') {
    style = 'bg-yellow-500';
    Icon = PencilIcon;
  }

  if (status === 'submitted') {
    style = 'bg-blue-500';
    Icon = ArrowUpCircleIcon;
  }

  if (status === 'published') {
    style = 'bg-green-500';
    Icon = CheckIcon;
  }

  if (status === 'rejected') {
    style = 'bg-red-500';
    Icon = ExclamationCircleIcon;
  }

  if (status === 'negociated') {
    style = 'bg-green-800';
    Icon = ChatBubbleLeftRightIcon;
  }

  if (status === 'closed' || status === 'sold') {
    style = 'bg-slate-500';
    text = t('status.closed');
    Icon = ArchiveBoxIcon;
  }

  if (status === 'cancelled') {
    style = 'bg-red-500';
    Icon = ExclamationCircleIcon;
  }

  // Status for users

  if (status === 'Authenticated') {
    style = 'bg-blue-300';
    text = t('roles.Authenticated');
    Icon = RiUserLine;
  }

  if (status === 'Admin') {
    style = 'bg-blue-400';
    text = t('roles.Administrateur');
    Icon = RiUserSettingsLine;
  }

  if (status === 'SuperAdmin') {
    style = 'bg-blue-500';
    text = t('roles.SuperAdmin');
    Icon = RiAdminLine;
  }

  // blocage utilisateur

  if (status === 'blocked') {
    style = 'bg-none';
    Icon = NoSymbolIcon;
    logoStyle = 'w-5 h-5 text-red-500';
    baseStyle = 'py-1 px-2 h-8 text-[1rem]';
    noText = true;
  }

  if (status === 'active') {
    style = 'bg-none';
    Icon = NoSymbolIcon;
    logoStyle = 'w-5 h-5 text-gray-200';
    baseStyle = 'py-1 px-2 h-8 text-[1rem]';
    noText = true;
  }

  // validation mail utilisateur

  if (status === 'email-not-validated') {
    style = 'bg-none';
    Icon = EnvelopeIcon;
    logoStyle = 'w-5 h-5 text-red-500';
    baseStyle = 'py-1 px-2 h-8 text-[1rem]';
    noText = true;
  }

  if (status === 'email-validated') {
    style = 'bg-none';
    Icon = EnvelopeIcon;
    logoStyle = 'w-5 h-5 text-green-500';
    baseStyle = 'py-1 px-2 h-8 text-[1rem]';
    noText = true;
  }

  // validation étudiant

  if (status === 'student-not-validated') {
    style = 'bg-amber-300';
    Icon = BiQuestionMark;
    logoStyle = 'w-5 h-5 text-slate-700';
    baseStyle = 'py-1 px-2 h-8 text-[1rem] rounded-lg';
    noText = true;
  }

  if (status === 'student-validated') {
    style = 'bg-green-500';
    Icon = CheckIcon;
    logoStyle = 'w-5 h-5 text-white';
    baseStyle = 'py-1 px-2 h-8 text-[1rem] rounded-lg';
    noText = true;
  }

  if (status === 'not-confirmed') {
    style = 'bg-none';
    Icon = FaQuestion;
    logoStyle = 'w-5 h-5 text-emerald-500';
    baseStyle = 'py-1 px-2 h-8 text-[1rem]';
    noText = true;
  }

  return useTooltip ? (
    <TooltipWrapper
      className="mr-1 my-1"
      content={
        <span>{i18n.exists(`status.${status}Tooltip`) ? t(`status.${status}Tooltip`) : text}</span>
      }
      place="top"
    >
      <div
        className={`flex items-center gap-1 font-normal leading-0 text-center text-white rounded-full ${baseStyle} ${style} shrink-0`}
      >
        {Icon && <Icon title={text} className={`${logoStyle}`} />}
        {!noText ? ` ${text}` : null}
      </div>
    </TooltipWrapper>
  ) : (
    <div
      className={`flex items-center gap-1 font-normal leading-0 text-center text-white rounded-full ${baseStyle} ${style} shrink-0`}
    >
      {Icon && <Icon title={text} className={`${logoStyle}`} />}
      {!noText ? ` ${text}` : null}
    </div>
  );
}

export default Status;
